import { useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";

const STEPS = ["Home", "System", "Job", "Contact", "Result"];

export default function EstimateWizard() {
  const { t, lang } = useLang();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState({ home_size: "", system_type: "AC", job_type: "Replacement", efficiency: "Standard", city: "", name: "", phone: "", email: "", notes: "" });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const next = () => setStep(s => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));

  const submit = async () => {
    setLoading(true);
    try {
      const r = await api.post("/estimate", { ...form, language: lang });
      setResult(r.data);
      setStep(STEPS.length - 1);
      toast.success("Estimate ready");
    } catch (e) {
      toast.error("Could not generate estimate");
    } finally { setLoading(false); }
  };

  return (
    <section id="estimate" className="py-12 md:py-20 bg-ink-800/30" data-testid="estimate">
      <div className="mx-auto max-w-7xl px-5 md:px-8 grid lg:grid-cols-2 gap-8 lg:gap-10 items-start">
        <div>
          <div className="text-xs font-bold uppercase tracking-[0.22em] text-ice-400">Instant Estimate</div>
          <h2 className="mt-3 font-display text-3xl md:text-4xl font-black">{t("estimate.title")}</h2>
          <p className="mt-3 text-silver-300 max-w-md">{t("estimate.sub")}</p>
          <ul className="mt-5 grid gap-2.5 text-sm text-silver-300">
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-ice-400 mt-0.5"/> Transparent price range — no surprises.</li>
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-ice-400 mt-0.5"/> Free in-home consultation included.</li>
            <li className="flex gap-2"><CheckCircle2 className="size-4 text-ice-400 mt-0.5"/> Multi-brand options to fit your budget.</li>
          </ul>
        </div>

        <div className="rounded-3xl bg-ink-800 ring-1 ring-white/10 p-6 md:p-8" data-testid="estimate-card">
          <div className="flex items-center gap-2 mb-5">
            {STEPS.map((_, i) => (
              <div key={i} className={`h-1 flex-1 rounded-full ${i <= step ? "bg-ice-400" : "bg-white/10"}`}/>
            ))}
          </div>

          {step === 0 && (
            <div className="grid gap-3">
              <label className="text-xs uppercase tracking-[0.2em] text-silver-400">Home size</label>
              <div className="grid grid-cols-3 gap-2">
                {["<1500 sqft","1500–2500","2500–4000+"].map(o => (
                  <button key={o} data-testid={`opt-size-${o}`} onClick={()=>set("home_size",o)} className={`px-3 py-3 rounded-xl text-sm ring-1 ${form.home_size===o ? "ring-ice-400 bg-ice-400/10":"ring-white/10 hover:ring-white/20"}`}>{o}</button>
                ))}
              </div>
            </div>
          )}
          {step === 1 && (
            <div className="grid gap-3">
              <label className="text-xs uppercase tracking-[0.2em] text-silver-400">Current system</label>
              <div className="grid grid-cols-2 gap-2">
                {["AC","Furnace","Heat Pump","Mini Split"].map(o => (
                  <button key={o} data-testid={`opt-sys-${o}`} onClick={()=>set("system_type",o)} className={`px-3 py-3 rounded-xl text-sm ring-1 ${form.system_type===o ? "ring-ice-400 bg-ice-400/10":"ring-white/10 hover:ring-white/20"}`}>{o}</button>
                ))}
              </div>
            </div>
          )}
          {step === 2 && (
            <div className="grid gap-3">
              <label className="text-xs uppercase tracking-[0.2em] text-silver-400">Job type & efficiency</label>
              <div className="grid grid-cols-2 gap-2">
                {["Repair","Replacement"].map(o => (
                  <button key={o} data-testid={`opt-job-${o}`} onClick={()=>set("job_type",o)} className={`px-3 py-3 rounded-xl text-sm ring-1 ${form.job_type===o ? "ring-ice-400 bg-ice-400/10":"ring-white/10 hover:ring-white/20"}`}>{o}</button>
                ))}
              </div>
              <div className="grid grid-cols-3 gap-2 mt-2">
                {["Standard","High Efficiency","Premium"].map(o => (
                  <button key={o} data-testid={`opt-eff-${o}`} onClick={()=>set("efficiency",o)} className={`px-3 py-3 rounded-xl text-sm ring-1 ${form.efficiency===o ? "ring-ice-400 bg-ice-400/10":"ring-white/10 hover:ring-white/20"}`}>{o}</button>
                ))}
              </div>
            </div>
          )}
          {step === 3 && (
            <div className="grid gap-3">
              <input placeholder="Your name" data-testid="est-name" className="bg-ink-700 ring-1 ring-white/10 rounded-xl px-4 py-3 text-sm" value={form.name} onChange={e=>set("name",e.target.value)} />
              <input placeholder="Phone" data-testid="est-phone" className="bg-ink-700 ring-1 ring-white/10 rounded-xl px-4 py-3 text-sm" value={form.phone} onChange={e=>set("phone",e.target.value)} />
              <input placeholder="Email (optional)" data-testid="est-email" className="bg-ink-700 ring-1 ring-white/10 rounded-xl px-4 py-3 text-sm" value={form.email} onChange={e=>set("email",e.target.value)} />
              <input placeholder="City" data-testid="est-city" className="bg-ink-700 ring-1 ring-white/10 rounded-xl px-4 py-3 text-sm" value={form.city} onChange={e=>set("city",e.target.value)} />
              <textarea placeholder="Notes (optional)" data-testid="est-notes" className="bg-ink-700 ring-1 ring-white/10 rounded-xl px-4 py-3 text-sm min-h-[80px]" value={form.notes} onChange={e=>set("notes",e.target.value)} />
            </div>
          )}
          {step === 4 && result && (
            <div className="grid gap-3" data-testid="est-result">
              <div className="rounded-2xl bg-ice-400/10 ring-1 ring-ice-400/30 p-5">
                <div className="text-xs uppercase tracking-[0.2em] text-ice-400">Estimated Range</div>
                <div className="font-display text-3xl md:text-4xl font-black mt-1">${result.estimated_low.toLocaleString()} – ${result.estimated_high.toLocaleString()}</div>
                <p className="text-xs text-silver-400 mt-2">Final pricing depends on installation requirements and equipment availability.</p>
              </div>
              <p className="text-sm text-silver-300">We&apos;ll text or call you shortly to confirm a free in-home consultation.</p>
            </div>
          )}

          <div className="mt-6 flex items-center justify-between gap-2">
            {step > 0 && step < 4 && (
              <button onClick={back} className="btn-ghost rounded-full px-4 py-2 text-sm flex items-center gap-1" data-testid="est-back"><ArrowLeft className="size-4"/> Back</button>
            )}
            <div className="flex-1" />
            {step < 3 && (
              <button onClick={next} disabled={(step===0&&!form.home_size)||(step===1&&!form.system_type)} className="btn-primary rounded-full px-5 py-2.5 text-sm flex items-center gap-1 disabled:opacity-50" data-testid="est-next">Next <ArrowRight className="size-4"/></button>
            )}
            {step === 3 && (
              <button onClick={submit} disabled={loading||!form.name||!form.phone} className="btn-primary rounded-full px-5 py-2.5 text-sm flex items-center gap-1 disabled:opacity-50" data-testid="est-submit">{loading?"Calculating...":"Get Estimate"} <ArrowRight className="size-4"/></button>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
